/**
 * ProtectedRoute.js - Route protection component for authenticated users
 *
 * Provides authentication-based route protection:
 * - Blocks access to protected routes for unauthenticated users
 * - Redirects unauthenticated users to login page
 * - Preserves intended destination for post-login redirect
 * - Allows special access to invite completion flow
 * - Handles edge cases for user onboarding flows
 *
 * Note: We intentionally avoid any full-screen loading/splash UI during
 * startup/login. While auth state is loading, we render nothing to prevent
 * disruptive overlays.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';

/**
 * ProtectedRoute - Authentication wrapper for protected routes
 *
 * @param {{ children: React.ReactNode }} props - Component props
 * @returns {React.ReactNode}
 *
 * Authentication flow:
 * 1. Shows loading spinner while checking auth state
 * 2. Redirects unauthenticated users to login with return path
 * 3. Allows special access to invite completion flow
 * 4. Renders protected content for authenticated users
 *
 * This component ensures that only authenticated users can access
 * protected routes while providing a smooth user experience.
 */
export const ProtectedRoute = ({ children }) => {
  /** @type {{ user: any, loading: boolean }} */
  const auth = /** @type {any} */ (useAuth()); // Get auth state from context
  const { user, loading } = auth;
  const location = useLocation(); // Current route location

  // While authentication state is being determined, render nothing.
  // SplashScreen is shown at App.js level to ensure minimum display time.
  if (loading) {
    return null;
  }

  // Redirect unauthenticated users to login page
  if (!user) {
    // Save the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Boot splash removed per UX request.

  // Special case: Allow access to invite completion even with incomplete profile
  // This is necessary for new invited users to complete their onboarding
  // after successfully processing an invite link
  if (location.pathname === '/invite-completion') {
    return children;
  }

  // For all other protected routes, render the protected content
  // Additional profile completeness checks could be added here if needed
  return children;
};
